import axios from "axios";
import { notify } from "components";

const BASE = "/tenant-management";

// ======================== App Connections ========================

export const loadAppConnections = async (search) => {
  try {
    let url = `${BASE}/app-connections/`;
    if (search) url += `?search=${search}`;
    const response = await axios.get(url);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading app connections:", err);
    notify("error", "Failed to load app connections");
  }
};

export const loadAppConnection = async (id) => {
  try {
    const response = await axios.get(`${BASE}/app-connections/${id}/`);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading app connection:", err);
    notify("error", "Failed to load app connection details");
  }
};

export const createAppConnection = async (data) => {
  try {
    const response = await axios.post(`${BASE}/app-connections/`, data);
    if (response.status === 201) {
      notify("success", "App connection created successfully");
      return response.data;
    }
  } catch (err) {
    console.error("Error creating app connection:", err);
    const msg = err.response?.data?.product?.[0] || err.response?.data?.detail || "Failed to create app connection";
    notify("error", msg);
  }
};

export const updateAppConnection = async (id, data) => {
  try {
    const response = await axios.patch(`${BASE}/app-connections/${id}/`, data);
    if (response.status === 200) {
      notify("success", "App connection updated successfully");
      return response.data;
    }
  } catch (err) {
    console.error("Error updating app connection:", err);
    notify("error", "Failed to update app connection");
  }
};

export const deleteAppConnection = async (id) => {
  try {
    const response = await axios.delete(`${BASE}/app-connections/${id}/`);
    if (response.status === 204) {
      notify("success", "App connection deleted");
      return true;
    }
  } catch (err) {
    console.error("Error deleting app connection:", err);
    notify("error", "Failed to delete app connection");
  }
};

export const testConnection = async (id) => {
  try {
    const response = await axios.post(`${BASE}/app-connections/${id}/test_connection/`);
    if (response.status === 200) {
      const { is_connected, message } = response.data;
      notify(is_connected ? "success" : "warning", message);
      return response.data;
    }
  } catch (err) {
    console.error("Error testing connection:", err);
    notify("error", err.response?.data?.message || "Connection test failed");
  }
};

export const refreshToken = async (id) => {
  try {
    const response = await axios.post(`${BASE}/app-connections/${id}/refresh_token/`);
    if (response.status === 200) {
      notify("success", response.data.message);
      return response.data;
    }
  } catch (err) {
    console.error("Error refreshing token:", err);
    notify("error", err.response?.data?.message || "Token refresh failed");
  }
};

export const syncTenants = async (id) => {
  try {
    const response = await axios.post(`${BASE}/app-connections/${id}/sync_tenants/`);
    if (response.status === 200) {
      notify("success", `Synced ${response.data.records_synced} tenants`);
      return response.data;
    }
  } catch (err) {
    console.error("Error syncing tenants:", err);
    notify("error", err.response?.data?.message || "Tenant sync failed");
  }
};

export const syncSubscriptions = async (id) => {
  try {
    const response = await axios.post(`${BASE}/app-connections/${id}/sync_subscriptions/`);
    if (response.status === 200) {
      notify("success", `Synced ${response.data.records_synced} subscriptions`);
      return response.data;
    }
  } catch (err) {
    console.error("Error syncing subscriptions:", err);
    notify("error", err.response?.data?.message || "Subscription sync failed");
  }
};

export const syncAll = async (id) => {
  try {
    const response = await axios.post(`${BASE}/app-connections/${id}/sync_all/`);
    if (response.status === 200) {
      notify("success", `Full sync complete: ${response.data.records_synced} records synced`);
      return response.data;
    }
  } catch (err) {
    console.error("Error syncing all:", err);
    notify("error", err.response?.data?.message || "Full sync failed");
  }
};

// ======================== Synced Tenants ========================

export const loadSyncedTenants = async (params = {}) => {
  try {
    const queryParts = [];
    if (params.app_connection) queryParts.push(`app_connection=${params.app_connection}`);
    if (params.status) queryParts.push(`status=${params.status}`);
    if (params.search) queryParts.push(`search=${params.search}`);
    if (params.page) queryParts.push(`page=${params.page}`);
    if (params.page_size) queryParts.push(`page_size=${params.page_size}`);
    if (params.ordering) queryParts.push(`ordering=${params.ordering}`);
    const query = queryParts.length ? `?${queryParts.join("&")}` : "";
    const response = await axios.get(`${BASE}/synced-tenants/${query}`);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading synced tenants:", err);
    notify("error", "Failed to load tenants");
  }
};

export const loadSyncedTenant = async (id) => {
  try {
    const response = await axios.get(`${BASE}/synced-tenants/${id}/`);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading synced tenant:", err);
    notify("error", "Failed to load tenant details");
  }
};

export const loadTenantUsers = async (id) => {
  try {
    const response = await axios.get(`${BASE}/synced-tenants/${id}/users/`);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading tenant users:", err);
    notify("error", err.response?.data?.message || "Failed to load tenant users");
    return { users: [], count: 0 };
  }
};

export const createTenant = async (data) => {
  try {
    const response = await axios.post(`${BASE}/synced-tenants/`, data);
    if (response.status === 201) {
      if (response.data.status === "setting_up") {
        notify("info", "Tenant is being created. Use 'Refresh Status' to check progress.");
      } else {
        notify("success", "Tenant created successfully");
      }
      return response.data;
    }
  } catch (err) {
    console.error("Error creating tenant:", err);
    const msg = err.response?.data?.message || err.response?.data?.detail || "Failed to create tenant";
    notify("error", msg);
  }
};

export const suspendTenant = async (id) => {
  try {
    const response = await axios.post(`${BASE}/synced-tenants/${id}/suspend/`);
    if (response.status === 200) {
      notify("success", "Tenant suspended");
      return response.data;
    }
  } catch (err) {
    console.error("Error suspending tenant:", err);
    notify("error", err.response?.data?.message || "Failed to suspend tenant");
  }
};

export const activateTenant = async (id) => {
  try {
    const response = await axios.post(`${BASE}/synced-tenants/${id}/activate/`);
    if (response.status === 200) {
      notify("success", "Tenant activated");
      return response.data;
    }
  } catch (err) {
    console.error("Error activating tenant:", err);
    notify("error", err.response?.data?.message || "Failed to activate tenant");
  }
};

export const refreshTenantStatus = async (id) => {
  try {
    const response = await axios.post(`${BASE}/synced-tenants/${id}/refresh_status/`);
    if (response.status === 200) {
      notify("success", `Tenant status: ${response.data.status}`);
      return response.data;
    }
  } catch (err) {
    console.error("Error refreshing tenant status:", err);
    notify("error", err.response?.data?.message || "Failed to refresh status");
  }
};

export const retryTenantSetup = async (id) => {
  try {
    const response = await axios.post(`${BASE}/synced-tenants/${id}/retry_setup/`);
    if (response.status === 200) {
      notify("success", "Tenant setup retried");
      return response.data;
    }
  } catch (err) {
    console.error("Error retrying tenant setup:", err);
    notify("error", err.response?.data?.message || "Failed to retry setup");
  }
};

// ======================== Synced Subscriptions ========================

export const loadSyncedSubscriptions = async (params = {}) => {
  try {
    const queryParts = [];
    if (params.app_connection) queryParts.push(`app_connection=${params.app_connection}`);
    if (params.status) queryParts.push(`status=${params.status}`);
    if (params.synced_tenant) queryParts.push(`synced_tenant=${params.synced_tenant}`);
    if (params.search) queryParts.push(`search=${params.search}`);
    if (params.page) queryParts.push(`page=${params.page}`);
    if (params.page_size) queryParts.push(`page_size=${params.page_size}`);
    if (params.ordering) queryParts.push(`ordering=${params.ordering}`);
    const query = queryParts.length ? `?${queryParts.join("&")}` : "";
    const response = await axios.get(`${BASE}/synced-subscriptions/${query}`);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading synced subscriptions:", err);
    notify("error", "Failed to load subscriptions");
  }
};

export const loadSyncedSubscription = async (id) => {
  try {
    const response = await axios.get(`${BASE}/synced-subscriptions/${id}/`);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading synced subscription:", err);
    notify("error", "Failed to load subscription details");
  }
};

// ======================== Sync Logs ========================

export const loadSyncLogs = async (params = {}) => {
  try {
    const queryParts = [];
    if (params.app_connection) queryParts.push(`app_connection=${params.app_connection}`);
    if (params.sync_type) queryParts.push(`sync_type=${params.sync_type}`);
    if (params.status) queryParts.push(`status=${params.status}`);
    if (params.page) queryParts.push(`page=${params.page}`);
    if (params.page_size) queryParts.push(`page_size=${params.page_size}`);
    const query = queryParts.length ? `?${queryParts.join("&")}` : "";
    const response = await axios.get(`${BASE}/sync-logs/${query}`);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading sync logs:", err);
    notify("error", "Failed to load sync logs");
  }
};

// ======================== Products (for dropdown) ========================

export const loadProducts = async () => {
  try {
    const response = await axios.get("/payment/pr/");
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading products:", err);
    notify("error", "Failed to load products");
  }
};

export const createProduct = async (data) => {
  try {
    const response = await axios.post("/payment/pr/", data);
    if (response.status === 201) {
      notify("success", "Product created successfully");
      return response.data;
    }
  } catch (err) {
    console.error("Error creating product:", err);
    const msg =
      err.response?.data?.code?.[0] ||
      err.response?.data?.name?.[0] ||
      err.response?.data?.detail ||
      "Failed to create product";
    notify("error", msg);
  }
};

export const updateProduct = async (id, data) => {
  try {
    const response = await axios.patch(`/payment/pr/${id}/`, data);
    if (response.status === 200) {
      notify("success", "Product updated successfully");
      return response.data;
    }
  } catch (err) {
    console.error("Error updating product:", err);
    notify("error", "Failed to update product");
  }
};

export const deleteProduct = async (id) => {
  try {
    const response = await axios.delete(`/payment/pr/${id}/`);
    if (response.status === 204) {
      notify("success", "Product deleted");
      return true;
    }
  } catch (err) {
    console.error("Error deleting product:", err);
    notify("error", "Failed to delete product");
  }
};

// ======================== Wizard onboarding (proxies into ERP) ========================

export const loadOnboardingPlans = async (connId) => {
  try {
    const response = await axios.get(`${BASE}/app-connections/${connId}/plans/`);
    if (response.status === 200) {
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    }
  } catch (err) {
    console.error("Error loading plans:", err);
    notify("error", err.response?.data?.message || "Failed to load plans");
    return [];
  }
};

export const loadOnboardingRoleTemplates = async (connId) => {
  try {
    const response = await axios.get(`${BASE}/app-connections/${connId}/role-templates/`);
    if (response.status === 200) {
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    }
  } catch (err) {
    console.error("Error loading role templates:", err);
    notify("error", err.response?.data?.message || "Failed to load role templates");
    return [];
  }
};

export const loadOnboardingPermissionGroups = async (connId) => {
  try {
    const response = await axios.get(`${BASE}/app-connections/${connId}/permission-groups/`);
    if (response.status === 200) {
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    }
  } catch (err) {
    console.error("Error loading permission groups:", err);
    notify("error", err.response?.data?.message || "Failed to load permission groups");
    return [];
  }
};

export const searchPaidInvoices = async (query) => {
  try {
    const params = new URLSearchParams({ status: "PAID", page_size: "20" });
    if (query) params.set("search", query);
    // The DRF router for the invoice app is mounted under /invoices/
    // and the InvoiceViewSet itself is registered at `invoices/`, so
    // the actual list URL is /invoices/invoices/. Hitting /invoices/
    // returns the API root index (no records).
    const response = await axios.get(`/invoices/invoices/?${params.toString()}`);
    if (response.status === 200) {
      const list = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);
      return list;
    }
    return [];
  } catch (err) {
    console.error("Error searching invoices:", err);
    notify(
      "error",
      err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to load invoices"
    );
    return [];
  }
};

export const checkSchemaNameAvailability = async (connId, schemaName) => {
  try {
    const response = await axios.get(
      `${BASE}/app-connections/${connId}/check-schema-name/`,
      { params: { schema_name: schemaName } },
    );
    if (response.status === 200) return response.data; // { available, schema_name }
  } catch (err) {
    console.error("Error checking schema name:", err);
    notify(
      "error",
      err.response?.data?.message || "Failed to check schema name availability",
    );
    return null;
  }
};

export const createTenantWithSetup = async (connId, payload) => {
  try {
    const response = await axios.post(
      `${BASE}/app-connections/${connId}/tenants-with-setup/`,
      payload,
    );
    if (response.status === 201) {
      notify("success", "Tenant onboarded successfully");
      return response.data;
    }
  } catch (err) {
    console.error("Error onboarding tenant:", err);
    const msg =
      err.response?.data?.message ||
      err.response?.data?.detail ||
      "Failed to onboard tenant";
    notify("error", msg);
    throw err;
  }
};

export const addTenantSubscription = async (syncedTenantId, payload) => {
  try {
    const response = await axios.post(
      `${BASE}/synced-tenants/${syncedTenantId}/add-subscription/`,
      payload,
    );
    if (response.status === 201) {
      notify("success", "Subscription added");
      return response.data;
    }
  } catch (err) {
    console.error("Error adding subscription:", err);
    notify(
      "error",
      err.response?.data?.message ||
        err.response?.data?.detail ||
        "Failed to add subscription",
    );
    throw err;
  }
};

export const loadTenantInvoices = async (syncedTenantId) => {
  try {
    const response = await axios.get(`${BASE}/synced-tenants/${syncedTenantId}/invoices/`);
    if (response.status === 200) return response.data;
  } catch (err) {
    console.error("Error loading tenant invoices:", err);
    notify("error", err.response?.data?.message || "Failed to load tenant invoices");
    return { invoices: [], count: 0 };
  }
};
